import { useState } from "react";
import { Link } from "react-router-dom";
import type { VideoItem } from "@/mocks/videos";
import { formatCount } from "@/utils/format";

interface VideoPlayerProps {
  video: VideoItem;
}

export default function VideoPlayer({ video }: VideoPlayerProps) {
  const [showNotice, setShowNotice] = useState(false);

  return (
    <div className="w-full">
      <div className="relative aspect-video w-full overflow-hidden rounded-lg border border-background-200 bg-foreground-950">
        <img
          src={video.cover}
          alt={video.title}
          title={`${video.title} 吃瓜视频`}
          className="h-full w-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/35 to-foreground-950/25" />

        <span className="absolute left-3 top-3 flex items-center gap-1 rounded bg-primary-500 px-2 py-1 text-[11px] font-medium text-background-50">
          <span className="flex h-3.5 w-3.5 items-center justify-center">
            <i className="ri-live-line text-sm" />
          </span>
          前端演示播放器
        </span>

        <button
          type="button"
          onClick={() => setShowNotice(true)}
          aria-label="播放"
          className="group absolute inset-0 flex flex-col items-center justify-center gap-3"
        >
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500 text-background-50 transition-transform group-hover:scale-110 md:h-20 md:w-20">
            <i className="ri-play-fill text-3xl md:text-4xl" />
          </span>
          <span className="text-sm text-background-50/90">点击播放（演示）</span>
        </button>

        <div className="absolute inset-x-0 bottom-0 px-3 pb-3 md:px-4 md:pb-4">
          <div className="h-1 w-full overflow-hidden rounded-full bg-background-50/30">
            <div className="h-full w-0 rounded-full bg-primary-500 transition-all duration-300 group-hover:w-1/4" />
          </div>
          <div className="mt-2 flex items-center justify-between text-xs text-background-50/85">
            <span>00:00 / {video.duration}</span>
            <span className="flex items-center gap-1">
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-play-line text-sm" />
              </span>
              {formatCount(video.views)} 次播放
            </span>
          </div>
        </div>
      </div>

      <p className="mt-3 flex items-start gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2 text-xs leading-5 text-foreground-600">
        <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-accent-600">
          <i className="ri-information-line text-base" />
        </span>
        温馨提示：当前播放器为前端静态演示，暂未接入真实视频流，点击播放不会加载实际视频内容。
      </p>

      {showNotice && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground-950/60 px-4"
          onClick={() => setShowNotice(false)}
        >
          <div
            className="w-full max-w-sm rounded-lg border border-background-200 bg-background-50 p-6 text-center animate-fade-up"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-3xl text-primary-500">
              <i className="ri-vidicon-line" />
            </span>
            <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">这是一个演示播放器</h3>
            <p className="mt-2 text-sm leading-6 text-foreground-600">
              本页面尚未接入真实视频源，播放按钮仅用于展示交互效果。后续接入视频流后即可正常观看
              「{video.title}」。
            </p>
            <button
              type="button"
              onClick={() => setShowNotice(false)}
              className="mt-5 w-full rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
            >
              我知道了
            </button>
            <Link
              to={`/category/${video.categoryId}`}
              className="mt-3 block text-xs text-accent-600 transition-colors hover:text-accent-700"
            >
              先去看看同分类的其他视频
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}